import { Buffer } from 'buffer';
import { useSnackbar } from 'notistack';
import { deflate, inflate } from 'pako';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import useFetch from 'use-http';
import {
  apiBridges,
  apiCollaborators,
  apiInspections,
  apiMedia,
  INSPECTIONS_ROOT,
} from 'constants/api';
import { InspectionMainTab, InspectionStatus } from 'constants/inspections';
import { STRUCTURES_PATHS } from 'constants/paths';
import { PermissionType } from 'constants/permissions';
import usePrevious from 'hooks/usePrevious';
import usePromises from 'hooks/usePromises';
import {
  useAuthSelector,
  useGlobalSelector,
  useImagesStore,
  useInspectionSelector,
} from 'stores';
import { mappingInAll } from './utils/mappingInAll';
/* import { mappingDataInL0 } from './utils/mappingInL0';
import { mappingDataInL1 } from './utils/mappingInL1'; */
import { mappingDataOutL0 } from './utils/mappingOutL0';
import { mappingDataOutL1 } from './utils/mappingOutL1';

const InspectionDisabledMapByPermission = {
  // this object map the value of the permission of the user
  // on the given inspection, and sets the "inspectionDisabled" store
  [PermissionType.CanView]: true,
  [PermissionType.CanEdit]: false,
  [PermissionType.CanViewByInspectionReference]: true,
  [PermissionType.NoPermission]: true,
};

const CanShareMap = {
  // this object maps the possibile actions on the sharing popover
  [InspectionStatus.NEW]: {
    [PermissionType.CanView]: false,
    [PermissionType.CanEdit]: true,
    [PermissionType.CanViewByInspectionReference]: false,
    [PermissionType.NoPermission]: false,
  },
  [InspectionStatus.ONGOING]: {
    [PermissionType.CanView]: false,
    [PermissionType.CanEdit]: true,
    [PermissionType.CanViewByInspectionReference]: false,
    [PermissionType.NoPermission]: false,
  },
  [InspectionStatus.OnGoing]: {
    [PermissionType.CanView]: false,
    [PermissionType.CanEdit]: true,
    [PermissionType.CanViewByInspectionReference]: false,
    [PermissionType.NoPermission]: false,
  },
  [InspectionStatus.Archived]: {
    [PermissionType.CanView]: false,
    [PermissionType.CanEdit]: false,
    [PermissionType.CanViewByInspectionReference]: false,
    [PermissionType.NoPermission]: false,
  },
  [InspectionStatus.Delivered]: {
    [PermissionType.CanView]: false,
    [PermissionType.CanEdit]: false,
    [PermissionType.CanViewByInspectionReference]: false,
    [PermissionType.NoPermission]: false,
  },
  [InspectionStatus.OnGoingDelivery]: {
    [PermissionType.CanView]: false,
    [PermissionType.CanEdit]: false,
    [PermissionType.CanViewByInspectionReference]: false,
    [PermissionType.NoPermission]: false,
  },
};

const useInspection = ({ inspectionId, structureId, openSendArchive }) => {
  const { put, get, error } = useFetch();
  const { enqueueSnackbar } = useSnackbar();
  const { showLoader, hideLoader, activeTabLevel } = useGlobalSelector();
  const prevActive = usePrevious(activeTabLevel);
  const navigate = useNavigate();
  const { setPromises } = usePromises();

  const { setInspectionDisabled, setL3ModuleCanEdit, setVersion, version } =
    useInspectionSelector();
  const { user } = useAuthSelector();

  const [structure, setStructure] = useState();
  const [inspection, setInspection] = useState();
  const [collaborators, setCollaborators] = useState([]);
  const [canShare, setCanShare] = useState(false);
  const [isStructureOwner, setIsStructureOwner] = useState(false);
  const [isInspectionOwner, setIsInspectionOwner] = useState(false);
  const [currentInspectionStatus, setCurrentInspectionStatus] = useState();
  const [inspectionStatus, setInspectionStatus] = useState();
  const [currentInspectionMyPermissions, setCurrentInspectionMyPermissions] =
    useState();
  const [webCompletionPercentage, setWebCompletionPercentage] = useState(0);
  const [appCompletionPercentage, setAppCompletionPercentage] = useState(0);
  const [accessExpirationDate, setAccessExpirationDate] = useState(null);

  const defaultValues = {};
  const methods = useForm({
    defaultValues,
  });

  /* START - Detail logic */

  // Get Detail of inspection
  const handleDetail = async () => {
    // e-2026-06-t21 — includeva solo L0/L1 perché finora ogni ingresso in
    // CreateInspection atterrava sempre su una di quelle due tab
    // (initialTab di default = 1), quindi `inspection`/`isL3Activity`
    // risultavano già popolati prima di eventuali click manuali su L2/L3.
    // Il bottone "Approfondisci" della tab L3 (Structures/StructureDetail/
    // Summary/TabL3/TabL3.jsx) atterra invece DIRETTAMENTE con
    // activeTabLevel L3 al primo mount: senza L2/L3 qui, `inspection` resta
    // undefined per sempre, `isL3Activity` risulta falsy e la tab L3 si
    // renderizza "disabled" (sbiadita) anche quando l'attività aperta è
    // realmente di tipo L3. mappingInAll/reset sotto restano condizionati
    // solo a L0/L1 (il form condiviso non serve a L2/L3, che leggono da
    // TabsL2/useInspectionL3 rispettivamente).
    if (
      prevActive !== activeTabLevel &&
      (activeTabLevel === InspectionMainTab.L0_CENSIMENTO_ANAGRAFICA ||
        activeTabLevel === InspectionMainTab.L1_ISPEZIONI_PRELIMINARI ||
        activeTabLevel === InspectionMainTab.L2_CLASSI_DI_ATTENZIONE ||
        activeTabLevel === InspectionMainTab.L3_IDRAULICA)
    ) {
      let result = '';
      try {
        showLoader();
        result = await get(`${apiBridges.INSPECTIONS}/${inspectionId}`);

        // unzip (inflate) diagnosis
        const inflatedDiagnosis = inflate(
          Buffer.from(result.diagnosis, 'base64'),
          {
            to: 'string',
          }
        );
        const parsedInflatedDiagnosis = JSON.parse(inflatedDiagnosis);
        result.diagnosis = parsedInflatedDiagnosis;

        // Il BE eredita l'intero diagnosis (L0/L1/L2 compresi) alla creazione
        // di qualunque attività, L3 incluso — result ha già tutto quello che
        // serve, nessun fetch aggiuntivo sull'attività L0,1,2 precedente.
        let formattedData = {};
        if (activeTabLevel === InspectionMainTab.L0_CENSIMENTO_ANAGRAFICA) {
          //formattedData = mappingDataInL0(result);
          formattedData = mappingInAll(result);
        }
        if (activeTabLevel === InspectionMainTab.L1_ISPEZIONI_PRELIMINARI) {
          //formattedData = mappingDataInL1(result);
          formattedData = mappingInAll(result);
        }

        setStructure(result?.structure);
        setInspection(result);
        setVersion(result?.version);
        reset(formattedData);

        const resultMedia = await get(
          `${apiMedia.LIST}/${inspectionId}/images`
        );
        if (resultMedia) {
          useImagesStore.setState(() => ({
            images: resultMedia,
          }));
        }
      } catch (err) {
        navigate(STRUCTURES_PATHS.STRUCTURES_LIST);
        console.error('Error in read diagnosis: ', err);
        return enqueueSnackbar(
          `Si è verificato un errore nella lettura dell'ispezione`,
          {
            variant: 'error',
          }
        );
      } finally {
        hideLoader();
      }
    }
  };

  //Const data Structure
  // Issue #1021, punto 4 — "[Nome opera] - [Tipologia attività]": il
  // suffisso va sul titolo dell'ATTIVITÀ (inspection.involvedLevels, es.
  // 'L3' o 'L0,L1,L2'), non su quello del ponte (che in StructureDetail.jsx
  // usa structure.involvedLevels, un campo diverso e aggregato a livello di
  // ponte, non della singola attività). Stessa formattazione "Schede X" già
  // usata lì per coerenza visiva.
  // Issue #1021, riapertura 2026-09-04, punto 4 — "Schede L3" da solo non
  // basta, il cliente chiede "Schede L3 Idraulica" (involvedLevels per le
  // attività L3 è la stringa letterale 'L3', non un'etichetta già estesa).
  const involvedLevelsSuffix = inspection?.involvedLevels
    ? ` - Schede ${
        inspection.involvedLevels === 'L3'
          ? 'L3 Idraulica'
          : inspection.involvedLevels
      }`
    : '';
  const titlePage =
    `Attività ${structure?.name || ''}${involvedLevelsSuffix}` || '-';
  const city = structure?.province?.name;
  const council = structure?.council?.name;
  const address =
    structure?.address || structure?.province?.code || city || council
      ? `${structure?.address ? structure.address + ',' : ''} ${council} (${
          structure?.province?.code
        })`
      : '-';
  const internalCode = structure?.internalCode || '-';
  const owner = structure?.owner?.email || '-';
  const infoData = {
    street: address || '-',
    city: city ? `Comune di ${council}` : '-',
    code: internalCode ? `ID ${internalCode}` : '-',
    owner,
  };

  useEffect(() => {
    inspectionId && handleDetail().catch(console.error);
  }, [inspectionId, activeTabLevel]);

  /* END - Detail logic */

  /* START - RHF logic */

  const { handleSubmit, reset, formState, setValue } = methods;

  const onSubmit = useCallback(
    async submittedData => {
      //TO DO: gestire salvataggio L0/L1
      let body = '';
      if (activeTabLevel === InspectionMainTab.L0_CENSIMENTO_ANAGRAFICA) {
        body = {
          diagnosis: mappingDataOutL0(submittedData),
          status: inspectionStatus,
          completionPercentage: webCompletionPercentage,
          completionPercentageApp: appCompletionPercentage,
        };
      }

      if (activeTabLevel === InspectionMainTab.L1_ISPEZIONI_PRELIMINARI) {
        body = {
          diagnosis: mappingDataOutL1(submittedData),
          status: inspectionStatus,
          completionPercentage: webCompletionPercentage,
          completionPercentageApp: appCompletionPercentage,
        };
      }

      try {
        showLoader();
        // zip (deflate) diagnosis
        const compressedBody = deflate(JSON.stringify(body));
        const compressedBodyBuffer = Buffer.from(compressedBody);
        const compressedBodyBufferString =
          compressedBodyBuffer.toString('base64');

        body = { zip: compressedBodyBufferString, version: version };
        await put(`${apiBridges.INSPECTIONS}/${inspectionId}/diagnosis`, body);
        const result = await get(`${apiMedia.LIST}/${inspectionId}/images`);
        if (result) {
          useImagesStore.setState(() => ({
            images: result,
          }));
        }
        const resInspectionDates = await get(
          `${INSPECTIONS_ROOT}/${inspectionId}/next-inspection-dates`
        );
        // console.log(resInspectionDates, 'resInspectionDates');
        setValue(
          'dataProssimaIspezioneOrdinaria',
          resInspectionDates?.dataProssimaIspezioneOrdinaria
        );
        setValue(
          'dataProssimaIspezioneStraordinaria',
          resInspectionDates?.dataProssimaIspezioneStraordinaria
        );

        const toValidISOString = dateString => {
          const date = new Date(dateString);
          return isNaN(date) ? null : date.toISOString();
        };

        // ORDINARIA
        if (
          resInspectionDates?.dataProssimaIspezioneOrdinaria !== '' &&
          resInspectionDates?.systemNextOrdinaryInspectionDate !== ''
        ) {
          const dataProssimaIspezioneOrdinaria = toValidISOString(
            resInspectionDates?.dataProssimaIspezioneOrdinaria
          );
          const systemNextOrdinaryInspectionDate = toValidISOString(
            resInspectionDates?.systemNextOrdinaryInspectionDate
          );

          if (
            dataProssimaIspezioneOrdinaria === systemNextOrdinaryInspectionDate
          ) {
            setValue('tipologiaOrdinariaLineeGuida', true);
            setValue('tipologiaOrdinariaIspettore', false);
          } else {
            setValue('tipologiaOrdinariaIspettore', true);
            setValue('tipologiaOrdinariaLineeGuida', false);
          }
        } else {
          resInspectionDates?.dataProssimaIspezioneOrdinaria &&
            setValue('tipologiaOrdinariaIspettore', true);
        }

        // STRAORDINARIA
        if (
          resInspectionDates?.dataProssimaIspezioneStraordinaria !== '' &&
          resInspectionDates?.systemNextExtraordinaryInspectionDate !== ''
        ) {
          const dataProssimaIspezioneStraordinaria = toValidISOString(
            resInspectionDates?.dataProssimaIspezioneStraordinaria
          );
          const systemNextExtraordinaryInspectionDate = toValidISOString(
            resInspectionDates?.systemNextExtraordinaryInspectionDate
          );

          if (
            dataProssimaIspezioneStraordinaria ===
            systemNextExtraordinaryInspectionDate
          ) {
            setValue('tipologiaStraordinariaLineeGuida', true);
            setValue('tipologiaStraordinariaIspettore', false);
          } else {
            setValue('tipologiaStraordinariaIspettore', true);
            setValue('tipologiaStraordinariaLineeGuida', false);
          }
        } else {
          resInspectionDates?.dataProssimaIspezioneStraordinaria &&
            setValue('tipologiaStraordinariaIspettore', true);
        }
        return enqueueSnackbar(`Salvataggio effettuato`, {
          variant: 'success',
        });
      } catch (err) {
        console.error('Error in save diagnosis: ', err);
        return enqueueSnackbar(
          `Si è verificato un errore nel salvataggio dell'ispezione`,
          {
            variant: 'error',
          }
        );
      } finally {
        hideLoader();
      }
    },
    [webCompletionPercentage, appCompletionPercentage, activeTabLevel]
  );

  /* END - RHF logic */

  /* START - Permissions logic */

  // error in case of wrong permissions or other
  useEffect(() => {
    if (error?.message?.includes('500 - Internal Server Error')) {
      return;
    } else {
      error && navigate(STRUCTURES_PATHS.STRUCTURES_LIST);
    }
  }, [error]);

  const handleCollaborators = async () => {
    const res = await get(
      `${apiCollaborators.ROOT}/inspection/${inspectionId}`
    );
    setCollaborators(
      res
        .map(el => {
          return {
            ...el,
            collaborationInfo: {
              ...el?.sharedInspectionsWithMe[0],
              type: el?.collaboratorOf[0]?.type,
            },
          };
        })
        .filter(
          el => el.collaborationInfo?.permission === PermissionType.CanEdit
        )
    );
  };
  const handleMyPermissions = async () => {
    const res = await get(
      `${apiInspections.ROOT}/${inspectionId}${apiInspections.MY_PERMISSIONS}`
    );
    if (!res) {
      setCurrentInspectionMyPermissions(true);
      setCanShare(false);
    } else {
      if (res.accessExpiration) {
        setAccessExpirationDate(res.accessExpiration);
      }
      setCurrentInspectionMyPermissions(
        InspectionDisabledMapByPermission[res?.permission]
      );
      // Punto 8 (banner modulo L3 mancante, specifica designer 2026-09-01)
      // — senza il modulo L3 attivo (né sull'utente né sul proprietario del
      // ponte/dell'attività) non si può condividere un'attività L3, stessa
      // condizione già usata per inspectionDisabled più sotto
      // (isL3ActivityWithoutEditAccess). CanShareMap non lo sapeva: per gli
      // altri livelli (L0/1/2) l3Access è sempre assente, quindi il nuovo
      // AND non cambia nulla lì.
      const canShareL3 = !(
        inspection?.isL3Activity && !inspection?.l3Access?.canEdit
      );
      setCanShare(
        CanShareMap[inspection?.status][res?.permission] && canShareL3
      );
    }
  };

  const shareInspection = () => {
    handleCollaborators();
  };

  useEffect(() => {
    if (inspectionId && inspection) {
      setPromises([handleCollaborators(), handleMyPermissions()]);
    }
  }, [inspectionId, inspection]);

  useEffect(() => {
    if (user && structure && inspection) {
      if (
        user.id === structure?.owner?.id ||
        user.parent?.id === structure?.owner?.id
      ) {
        setIsStructureOwner(true);
      }
      if (
        user.id === inspection?.inspectionOwnerId ||
        user.parent?.id === inspection?.inspectionOwnerId
      ) {
        setIsInspectionOwner(true);
      }
      setCurrentInspectionStatus(
        inspection?.status === InspectionStatus.Archived ||
          inspection?.status === InspectionStatus.OnGoingDelivery ||
          inspection?.status === InspectionStatus.Delivered ||
          (inspection?.lockedAt && inspection?.lockedBy?.id !== user?.id)
      );
      setInspectionStatus(inspection?.status);
    }
  }, [user, structure, inspection]);

  useEffect(() => {
    // Attività L3: forza la sola lettura su L0/L1 indipendentemente dal
    // permesso dell'utente sull'attività stessa — vedi
    // apps/web-bo/plans/features/l3-modulo-attivita.md, punto 9. Nessun
    // effetto su un'Inspection "normale" (isL3Activity false).
    const isLowerLevelTabOnL3Activity =
      inspection?.isL3Activity &&
      (activeTabLevel === InspectionMainTab.L0_CENSIMENTO_ANAGRAFICA ||
        activeTabLevel === InspectionMainTab.L1_ISPEZIONI_PRELIMINARI);

    // Task 18 — l3Access è una proprietà del proprietario del ponte, non
    // dell'utente: se l'attività aperta è essa stessa L3 e il proprietario
    // ha perso il modulo, l'intera attività va in sola lettura (dati
    // visibili, Salva/Ricalcola CdA disabilitati) — stesso meccanismo già
    // usato sopra per L0/L1 dentro un'attività L3. Vedi
    // apps/web-bo/plans/features/l3/task-18-l3-condivisione-handoff-fe.md.
    const isL3ActivityWithoutEditAccess =
      inspection?.isL3Activity && !inspection?.l3Access?.canEdit;

    if (
      currentInspectionStatus ||
      isLowerLevelTabOnL3Activity ||
      isL3ActivityWithoutEditAccess
    ) {
      return setInspectionDisabled(true);
    }
    setInspectionDisabled(currentInspectionMyPermissions);
  }, [
    currentInspectionMyPermissions,
    currentInspectionStatus,
    activeTabLevel,
    inspection,
  ]);

  useEffect(() => {
    // l3Access è popolato sempre (anche su un'attività L0/1/2 normale) —
    // serve a gatare lo switch "Tipologia di calcolo" (Accordion4HydraulicRiskL1.jsx)
    // indipendentemente dal tipo dell'attività corrente.
    setL3ModuleCanEdit(inspection ? !!inspection?.l3Access?.canEdit : null);
  }, [inspection]);

  useEffect(() => {
    const handleStructureDetail = async () => {
      try {
        showLoader();
        const result = await get(`${apiBridges.ROOT}/${structureId}`);
        setStructure(result);

        const toValidISOString = dateString => {
          const date = new Date(dateString);
          return isNaN(date) ? null : date.toISOString();
        };

        const resInspectionDates = await get(
          `${INSPECTIONS_ROOT}/${inspectionId}/next-inspection-dates`
        );
        // console.log(resInspectionDates, 'resInspectionDates');
        setValue(
          'dataProssimaIspezioneOrdinaria',
          resInspectionDates?.dataProssimaIspezioneOrdinaria
        );
        setValue(
          'dataProssimaIspezioneStraordinaria',
          resInspectionDates?.dataProssimaIspezioneStraordinaria
        );
        if (
          resInspectionDates?.dataProssimaIspezioneOrdinaria !== '' &&
          resInspectionDates?.systemNextOrdinaryInspectionDate !== ''
        ) {
          const dataProssimaIspezioneOrdinaria = toValidISOString(
            resInspectionDates?.dataProssimaIspezioneOrdinaria
          );
          const systemNextOrdinaryInspectionDate = toValidISOString(
            resInspectionDates?.systemNextOrdinaryInspectionDate
          );

          if (
            dataProssimaIspezioneOrdinaria === systemNextOrdinaryInspectionDate
          ) {
            setValue('tipologiaOrdinariaLineeGuida', true);
            setValue('tipologiaOrdinariaIspettore', false);
          } else {
            setValue('tipologiaOrdinariaIspettore', true);
            setValue('tipologiaOrdinariaLineeGuida', false);
          }
        } else {
          resInspectionDates?.dataProssimaIspezioneOrdinaria &&
            setValue('tipologiaOrdinariaIspettore', true);
        }

        if (
          resInspectionDates?.dataProssimaIspezioneStraordinaria !== '' &&
          resInspectionDates?.systemNextExtraordinaryInspectionDate !== ''
        ) {
          const dataProssimaIspezioneStraordinaria = toValidISOString(
            resInspectionDates?.dataProssimaIspezioneStraordinaria
          );
          const systemNextExtraordinaryInspectionDate = toValidISOString(
            resInspectionDates?.systemNextExtraordinaryInspectionDate
          );

          if (
            dataProssimaIspezioneStraordinaria ===
            systemNextExtraordinaryInspectionDate
          ) {
            setValue('tipologiaStraordinariaLineeGuida', true);
            setValue('tipologiaStraordinariaIspettore', false);
          } else {
            setValue('tipologiaStraordinariaIspettore', true);
            setValue('tipologiaStraordinariaLineeGuida', false);
          }
        } else {
          resInspectionDates?.dataProssimaIspezioneStraordinaria &&
            setValue('tipologiaStraordinariaIspettore', true);
        }
      } catch (err) {
        console.error('Error in read diagnosis: ', err);
        return enqueueSnackbar(
          `Si è verificato un errore nella lettura dell'ispezione`,
          {
            variant: 'error',
          }
        );
      } finally {
        hideLoader();
      }
    };
    handleStructureDetail();
  }, [collaborators, openSendArchive]);

  /* END - Permissions logic */

  return {
    titlePage,
    structure,
    infoData,
    methods,
    handleSubmit,
    onSubmit,
    collaborators,
    shareInspection,
    canShare,
    isStructureOwner,
    isInspectionOwner,
    formState,
    inspectionStatus,
    setWebCompletionPercentage,
    setAppCompletionPercentage,
    accessExpirationDate,
    // Tipo dell'attività correntemente aperta (L0,L1,L2 vs L3) — flag BE già
    // presente nella GET dell'ispezione (InspectionExtensions.isL3Activity,
    // apps/api/.../inspection.extensions.ts), qui solo esposto per uso in
    // CreateInspection.jsx (tab L3 selezionabile solo su un'attività L3).
    isL3Activity: inspection?.isL3Activity,
  };
};

export default useInspection;
